// src/pages/NeedDetail.jsx
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { doc, getDoc, addDoc, collection, serverTimestamp, updateDoc, increment } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../lib/AuthContext";
import { formatDistanceToNow } from "date-fns";

export default function NeedDetail() {
  const { id } = useParams();
  const { currentUser, userProfile, effectiveRole } = useAuth();
  const navigate = useNavigate();
  const [need, setNeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pitchForm, setPitchForm] = useState({ summary: "", approach: "", timeline: "", budget: "", whyUs: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const backPath = effectiveRole === "corporation" ? "/my-needs" : "/feed";
  const backLabel = effectiveRole === "corporation" ? "Back to my challenges" : "Back to feed";

  useEffect(() => {
    async function load() {
      const snap = await getDoc(doc(db, "needs", id));
      if (snap.exists()) setNeed({ id: snap.id, ...snap.data() });
      setLoading(false);
    }
    load();
  }, [id]);

  function set(field) { return e => setPitchForm(f => ({ ...f, [field]: e.target.value })); }

  async function handlePitch(e) {
    e.preventDefault();
    if (!currentUser) { navigate("/login"); return; }
    setSubmitting(true); setError("");
    try {
      await addDoc(collection(db, "pitches"), {
        ...pitchForm,
        needId: id,
        needTitle: need.title,
        corpId: need.corpId,
        corpName: need.corpName,
        startupId: currentUser.uid,
        startupName: userProfile?.orgName || userProfile?.displayName,
        startupContact: userProfile?.displayName,
        startupEmail: currentUser.email,
        status: "pending",
        createdAt: serverTimestamp()
      });
      await updateDoc(doc(db, "needs", id), { pitchCount: increment(1) });
      setSubmitted(true);
    } catch (err) {
      setError("Failed to submit pitch. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <div className="page-loading"><div className="spinner" style={{ width: 28, height: 28 }}></div></div>;
  if (!need) return <div style={{ padding: 40, textAlign: "center" }}>Challenge not found. <Link to={backPath} style={{ color: "var(--green)" }}>{backLabel}</Link></div>;

  const timeAgo = need.createdAt?.toDate ? formatDistanceToNow(need.createdAt.toDate(), { addSuffix: true }) : "recently";

  return (
    <div className="app-shell detail-layout">
      {/* Left: need details */}
      <div>
        <Link to={backPath} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, color: "var(--text-3)", marginBottom: 20 }}>
          ← {backLabel}
        </Link>

        <div className="card" style={{ padding: "24px" }}>
          {/* Corp header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div className="avatar" style={{
                width: 44, height: 44, fontSize: 14,
                background: need.avatarColor || "#f0f0f0",
                color: need.avatarTextColor || "var(--text-2)"
              }}>{need.corpAvatar || need.corpName?.slice(0, 2).toUpperCase()}</div>
              <div>
                <div className="need-card-org">{need.corpName}</div>
                <div className="need-card-industry">{need.industry} · posted {timeAgo}</div>
              </div>
            </div>
            <StatusBadge status={need.status} />
          </div>

          <h1 style={{ fontSize: 26, fontWeight: 700, marginBottom: 12, letterSpacing: "-0.02em", lineHeight: 1.3 }}>{need.title}</h1>
          <p className="prose-body" style={{ marginBottom: 20 }}>{need.description}</p>

          {/* Meta grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
            {[
              { label: "Budget", value: need.budget },
              { label: "Timeline", value: need.timeline },
              { label: "Startup stage", value: need.startupStage || "Any stage" },
              { label: "Solutions received", value: `${need.pitchCount || 0}` },
            ].map(item => (
              <div key={item.label} style={{
                padding: "12px 14px", background: "var(--bg)", borderRadius: "var(--radius)",
                border: "1px solid var(--border)"
              }}>
                <div style={{ fontSize: 12, color: "var(--text-3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{item.label}</div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>{item.value}</div>
              </div>
            ))}
          </div>

          {need.tags?.length > 0 && (
            <div>
              <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 8 }}>Looking for expertise in</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {need.tags.map(t => <span key={t} className="tag" style={{ fontSize: 13, padding: "4px 10px" }}>{t}</span>)}
              </div>
            </div>
          )}

          {need.additionalContext && (
            <div style={{ marginTop: 20, padding: "14px", background: "var(--bg)", borderRadius: "var(--radius)", borderLeft: "3px solid var(--green)" }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: "var(--text-3)", marginBottom: 6 }}>Additional context</div>
              <p className="prose-body prose-body--sm">{need.additionalContext}</p>
            </div>
          )}
        </div>
      </div>

      {/* Right: pitch form */}
      <div style={{ position: "sticky", top: 80 }}>
        {!currentUser ? (
          <div className="card" style={{ padding: 24, textAlign: "center" }}>
            <h3 style={{ fontSize: 16, marginBottom: 8 }}>Ready to propose a solution?</h3>
            <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 16 }}>Sign in or create a startup account to submit your solution.</p>
            <Link to="/login" className="btn btn-primary" style={{ width: "100%", justifyContent: "center", marginBottom: 8 }}>Sign in to submit a solution</Link>
            <Link to="/signup?role=startup" className="btn btn-outline" style={{ width: "100%", justifyContent: "center" }}>Create startup account</Link>
          </div>
        ) : userProfile?.role === "corporation" ? (
          <div className="card" style={{ padding: 24, textAlign: "center" }}>
            <p style={{ fontSize: 13, color: "var(--text-2)" }}>You're signed in as a corporation. Only startup accounts can submit solutions to challenges.</p>
          </div>
        ) : submitted ? (
          <div className="card" style={{ padding: 28, textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🎉</div>
            <h3 style={{ fontSize: 18, marginBottom: 8 }}>Solution submitted!</h3>
            <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 20 }}>{need.corpName} will review your solution and reach out if there's a match.</p>
            <Link to="/my-pitches" className="btn btn-outline" style={{ width: "100%", justifyContent: "center", marginBottom: 8 }}>View my solutions</Link>
            <Link to="/feed" className="btn btn-ghost" style={{ width: "100%", justifyContent: "center" }}>Back to feed</Link>
          </div>
        ) : (
          <div className="card" style={{ padding: 20 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Submit your solution</h3>
            <form onSubmit={handlePitch} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Solution summary <span style={{ color: "#d32f2f" }}>*</span></label>
                <textarea className="textarea" style={{ minHeight: 80 }} value={pitchForm.summary} onChange={set("summary")} placeholder="In 2–3 sentences, what does your solution do?" required />
              </div>
              <div className="form-group">
                <label className="form-label">Your approach <span style={{ color: "#d32f2f" }}>*</span></label>
                <textarea className="textarea" value={pitchForm.approach} onChange={set("approach")} placeholder="How would you tackle this specific challenge?" required />
              </div>
              <div className="form-group">
                <label className="form-label">Proposed timeline</label>
                <input className="input" value={pitchForm.timeline} onChange={set("timeline")} placeholder="e.g. MVP in 6 weeks, full rollout in 3 months" />
              </div>
              <div className="form-group">
                <label className="form-label">Proposed budget</label>
                <input className="input" value={pitchForm.budget} onChange={set("budget")} placeholder="e.g. $75,000 for initial deployment" />
              </div>
              <div className="form-group">
                <label className="form-label">Why your startup? <span style={{ color: "#d32f2f" }}>*</span></label>
                <textarea className="textarea" style={{ minHeight: 70 }} value={pitchForm.whyUs} onChange={set("whyUs")} placeholder="Relevant experience, clients, or traction" required />
              </div>
              {error && <div className="form-error">{error}</div>}
              <button className="btn btn-primary" type="submit" disabled={submitting} style={{ justifyContent: "center", padding: "10px" }}>
                {submitting ? <span className="spinner" style={{ width: 16, height: 16 }}></span> : "Submit solution →"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === "new") return <span className="badge badge-purple">New</span>;
  if (status === "open") return <span className="badge badge-green">Open</span>;
  if (status === "closing") return <span className="badge badge-amber">Closing soon</span>;
  return null;
}
